import { useEffect, useState } from 'react'
import { EyeOff, Lock, Trash2 } from '@digital-twins/design-system/icons'
import { useTranslation } from 'react-i18next'
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { populationsApi } from '@/lib/api'
import { useAuth } from '@/lib/config/auth-provider'
import { ROUTES } from '@/routes/routes'

interface MarketplaceRemoveDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  audienceId: string
  audienceName: string
  onDeactivate: () => void
  onMakePrivate: () => Promise<void>
  onDelete: () => void
}

export default function MarketplaceRemoveDialog({
  open,
  onOpenChange,
  audienceId,
  audienceName,
  onDeactivate,
  onMakePrivate,
  onDelete,
}: MarketplaceRemoveDialogProps) {
  const { t } = useTranslation('app')
  const { user } = useAuth()
  const [creditCost, setCreditCost] = useState<number | null>(null)
  const [insufficientCredits, setInsufficientCredits] = useState(false)
  const [processing, setProcessing] = useState(false)

  const isAdmin = user?.role === 'admin' || user?.role === 'owner'

  useEffect(() => {
    if (!open || !audienceId) return
    let cancelled = false
    setCreditCost(null)
    setInsufficientCredits(false)
    populationsApi
      .getMarketplaceCreditCost(audienceId)
      .then((res) => { if (!cancelled) setCreditCost(res.credits) })
      .catch(() => { if (!cancelled) setCreditCost(null) })
    return () => { cancelled = true }
  }, [open, audienceId])

  const handleMakePrivate = async () => {
    setProcessing(true)
    setInsufficientCredits(false)
    try {
      await onMakePrivate()
      onOpenChange(false)
    } catch (error: any) {
      const code = error?.response?.data?.code
      if (code === 'INSUFFICIENT_CREDITS') {
        setInsufficientCredits(true)
      } else {
        const { toast } = await import('sonner')
        toast.error('Failed to make audience private. Please try again.')
      }
    } finally {
      setProcessing(false)
    }
  }

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="rounded-2xl sm:max-w-[480px]">
        <AlertDialogHeader>
          <AlertDialogTitle>{t('audienceActions.removeMarketplace')}</AlertDialogTitle>
          <AlertDialogDescription>
            Choose how to handle <strong>{audienceName}</strong>.
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="flex flex-col gap-3 py-2">
          <Button
            variant="outline"
            className="justify-start gap-3 h-auto py-3 px-4 text-left"
            onClick={() => {
              onDeactivate()
              onOpenChange(false)
            }}
          >
            <EyeOff className="h-4 w-4 shrink-0" />
            <div>
              <div className="font-medium">{t('audienceActions.deactivate')}</div>
              <div className="text-sm text-muted-foreground font-normal">
                Hide from marketplace. You can reactivate anytime for free.
              </div>
            </div>
          </Button>

          <Button
            variant="outline"
            className="justify-start gap-3 h-auto py-3 px-4 text-left"
            onClick={handleMakePrivate}
            disabled={processing}
          >
            <Lock className="h-4 w-4 shrink-0" />
            <div>
              <div className="font-medium">
                {t('audienceActions.makePrivate')}
                {creditCost != null && creditCost > 0 && (
                  <span className="font-normal text-muted-foreground">
                    {' '}— costs {creditCost} credits
                  </span>
                )}
              </div>
              <div className="text-sm text-muted-foreground font-normal">
                Remove from marketplace permanently. Credits will be charged.
              </div>
            </div>
          </Button>

          {insufficientCredits && (
            <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm">
              <p className="text-destructive font-medium mb-1">
                {t('audienceActions.insufficientCredits')}
              </p>
              {isAdmin ? (
                <p className="text-muted-foreground">
                  You don&apos;t have enough credits.{' '}
                  <a
                    href={ROUTES.billingPlans}
                    className="text-primary underline"
                  >
                    Top up your credits
                  </a>{' '}
                  or enable overage to proceed.
                </p>
              ) : (
                <p className="text-muted-foreground">
                  You don&apos;t have enough credits. Contact your admin to
                  enable overage or top up credits.
                </p>
              )}
            </div>
          )}

          <Button
            variant="outline"
            className="justify-start gap-3 h-auto py-3 px-4 text-left border-destructive/30 text-destructive hover:bg-destructive/5"
            onClick={() => {
              onDelete()
              onOpenChange(false)
            }}
          >
            <Trash2 className="h-4 w-4 shrink-0" />
            <div>
              <div className="font-medium">{t('audienceActions.delete')}</div>
              <div className="text-sm text-muted-foreground font-normal">
                Permanently delete this audience. This cannot be undone.
              </div>
            </div>
          </Button>
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel className="rounded-full">{t('cancel', { ns: 'common' })}</AlertDialogCancel>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
